import React from 'react'
import moment from 'moment'
import { Chip, Stack, Typography } from '@mui/material'

// Shared bits for the DOPS (ProcedureAssessment) and logbook pages - see
// server/src/routers/patientReports/procedureAssessments.js.

export const DOPS_STATUS_COLORS = {
  Requested: 'warning',
  Completed: 'success',
  Declined: 'error',
  Withdrawn: 'default',
}

export const DopsStatusChip = ({ status, size = 'small' }) => (
  <Chip
    size={size}
    label={status}
    color={DOPS_STATUS_COLORS[status] ?? 'default'}
    variant={status === 'Completed' ? 'filled' : 'outlined'}
    sx={{ borderRadius: 1, fontWeight: 700 }}
  />
)

export const OVERALL_COLORS = {
  'Needs further supervised practice': 'warning',
  'Competent with indirect supervision': 'info',
  'Competent to perform independently': 'success',
}

export const personName = (person) =>
  person ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() : ''

export const formatDate = (date, format = 'DD/MM/YYYY') =>
  date ? moment(date).format(format) : ''

// Server errors come back as { error } (4xx) or the ERROR helper's shape
export const apiError = (res, fallback) =>
  res?.data?.error ||
  res?.data?.message ||
  (typeof res?.data === 'string' && res.data.length < 200 ? res.data : '') ||
  fallback

// Snapshot of the ePCR entry: render generically as label: value, skipping
// empty values and the timestamp (shown separately)
const SNAPSHOT_SKIP = new Set(['dateTime', 'time', 'moduleName', '_id'])

export const snapshotRows = (snapshot) =>
  Object.entries(snapshot ?? {})
    .filter(([key, value]) => {
      if (SNAPSHOT_SKIP.has(key)) return false
      if (value === null || value === undefined || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      return true
    })
    .map(([key, value]) => ({
      label: key,
      value: Array.isArray(value)
        ? value
            .map((item) =>
              item && typeof item === 'object'
                ? Object.values(item).filter(Boolean).join(' ')
                : String(item),
            )
            .join(', ')
        : typeof value === 'object'
          ? Object.entries(value)
              .filter(([, v]) => v !== null && v !== undefined && v !== '')
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ')
          : typeof value === 'boolean'
            ? value
              ? 'Yes'
              : 'No'
            : String(value),
    }))

// One-line description of a DOPS for lists
export const DopsSummary = ({ dops, role }) => (
  <Stack minWidth={0}>
    <Typography variant="body2" fontWeight={700} noWrap>
      {dops.procedureLabel || 'Procedure'}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap>
      {[
        role === 'assessor'
          ? personName(dops.trainee)
          : role === 'trainee'
            ? `Assessor: ${personName(dops.assessor)}`
            : `${personName(dops.trainee)} → ${personName(dops.assessor)}`,
        dops.performedAt ? formatDate(dops.performedAt) : null,
        dops.epcrNumber ? `ePCR ${dops.epcrNumber}` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
    </Typography>
  </Stack>
)
